import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, Check, MapPin } from "lucide-react";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "../components/ui/accordion";
import { Reveal, Eyebrow } from "../components/Reveal";
import { PageHero, FinalCta } from "../components/Sections";
import { metroBySlug, METROS, METRO_FAQS, HI_METROS, HI_METRO_FAQS } from "../data/metros";
import { HiFinalCta } from "./HindiHome";

const MetroMarket = ({ lang = "en" }) => {
    const { city: slug } = useParams();
    const base = metroBySlug(slug);
    const metro = base ? { ...base, ...(lang === "hi" ? HI_METROS[slug] || {} : {}) } : null;
    const faqs = metro ? (lang === "hi" ? HI_METRO_FAQS[slug] || METRO_FAQS[slug] : METRO_FAQS[slug]) : null;

    useEffect(() => {
        if (!metro) return;
        document.title = lang === "hi" ? `${metro.city} में मार्केटिंग और सेल्स ग्रोथ पार्टनर | ALBL` : metro.metaTitle;
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.id = "metro-schema";
        script.text = JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
                {
                    "@type": "Service",
                    serviceType: "Brand, marketing and sales growth services",
                    provider: {
                        "@type": "Organization",
                        name: "ALBL Communications Private Limited",
                    },
                    areaServed: { "@type": "City", name: metro.city },
                },
                ...(METRO_FAQS[metro.slug]
                    ? [{
                        "@type": "FAQPage",
                        mainEntity: METRO_FAQS[metro.slug].map((f) => ({
                            "@type": "Question",
                            name: f.q,
                            acceptedAnswer: { "@type": "Answer", text: f.a },
                        })),
                    }]
                    : []),
            ],
        });
        document.head.appendChild(script);
        return () => document.getElementById("metro-schema")?.remove();
    }, [metro]);

    if (!metro) {
        return (
            <main className="pt-40 pb-24 text-center">
                <h1 className="font-display text-3xl font-bold text-slate-900">Market not found.</h1>
                <Link to="/markets" className="mt-6 inline-block text-[#1E3A8A] text-sm font-semibold" data-testid="metro-not-found-back">
                    Back to Markets
                </Link>
            </main>
        );
    }

    const others = METROS.filter((m) => m.slug !== slug);

    return (
        <main>
            <PageHero
                eyebrow={lang === "hi" ? `बाज़ार — ${metro.city}` : `Markets — ${metro.city}`}
                title={metro.heroTitle}
                sub={metro.heroSub}
            />

            <section className="bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-20 sm:py-28 grid lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-7">
                        <Reveal>
                            {metro.intro.map((p) => (
                                <p key={p.slice(0, 24)} className="text-slate-600 text-base sm:text-lg leading-relaxed mb-5">
                                    {p}
                                </p>
                            ))}
                        </Reveal>
                        <Reveal delay={120}>
                            <div className="mt-10 border border-slate-200 bg-[#F8FAFC] p-8">
                                <MapPin className="h-5 w-5 text-[#C5A059]" />
                                <p className="mt-4 text-slate-700 text-sm sm:text-base leading-relaxed">{metro.local}</p>
                            </div>
                        </Reveal>
                    </div>
                    <div className="lg:col-span-5">
                        <Reveal delay={150}>
                            <div className="bg-[#0D131F] text-white p-8 sm:p-10">
                                <Eyebrow dark>{metro.focusTitle}</Eyebrow>
                                <ul className="mt-6 space-y-5">
                                    {metro.focus.map((f) => (
                                        <li key={f} className="flex gap-3 items-start">
                                            <Check className="h-4 w-4 text-[#C5A059] mt-1 shrink-0" />
                                            <span className="text-slate-300 text-sm sm:text-base leading-relaxed">{f}</span>
                                        </li>
                                    ))}
                                </ul>
                                <Link
                                    to={`${lang === "hi" ? "/hi/contact" : "/contact"}?city=${metro.city}&intent=audit`}
                                    data-testid={`metro-cta-${metro.slug}`}
                                    className="btn-sharp mt-10 inline-flex items-center gap-2 bg-[#C5A059] text-[#0D131F] text-xs font-bold tracking-[0.15em] uppercase px-8 py-4"
                                >
                                    {lang === "hi" ? `${metro.city} में ग्रोथ ऑडिट लें` : `Get a Growth Audit in ${metro.city}`} <ArrowRight className="h-4 w-4" />
                                </Link>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            <section className="bg-[#F8FAFC]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-16 sm:py-20">
                    <Reveal>
                        <Eyebrow>{lang === "hi" ? "अन्य बड़े बाज़ार" : "Other metro markets"}</Eyebrow>
                        <div className="mt-8 grid sm:grid-cols-2 gap-4">
                            {others.map((m) => (
                                <Link
                                    key={m.slug}
                                    to={`${lang === "hi" ? "/hi" : ""}/markets/${m.slug}`}
                                    data-testid={`metro-link-${m.slug}`}
                                    className="group flex items-center justify-between border border-slate-200 bg-white px-6 py-5 hover:border-[#0D131F] transition-colors"
                                >
                                    <span className="font-display text-lg font-bold text-slate-900 group-hover:text-[#1E3A8A] transition-colors">
                                        {m.city}
                                    </span>
                                    <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-[#C5A059] group-hover:translate-x-1 transition-all" />
                                </Link>
                            ))}
                        </div>
                    </Reveal>
                </div>
            </section>

            {metro.photos && (
                <section className="bg-[#F8FAFC]">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-16 sm:py-20">
                        <Reveal>
                            <Eyebrow>{lang === "hi" ? `${metro.city} में हमारी मौजूदगी` : `On the ground in ${metro.city}`}</Eyebrow>
                            <div className="mt-8 grid sm:grid-cols-3 gap-4" data-testid="metro-photos">
                                {metro.photos.map((p) => (
                                    <figure key={p.src} className="border border-slate-200 bg-white">
                                        <img
                                            src={p.src}
                                            alt={p.alt}
                                            loading="lazy"
                                            className="w-full h-52 object-cover"
                                        />
                                        <figcaption className="px-4 py-3 text-xs text-slate-500">
                                            {p.caption}
                                        </figcaption>
                                    </figure>
                                ))}
                            </div>
                            <p className="mt-4 text-xs text-slate-400">
                                Photos: Wikimedia Commons contributors, CC-licensed.
                            </p>
                        </Reveal>
                    </div>
                </section>
            )}

            {faqs && (
                <section className="bg-white">
                    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 py-16 sm:py-24">
                        <Reveal>
                            <Eyebrow>Questions</Eyebrow>
                            <h2
                                className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-900 leading-[1.15] mt-5"
                                data-testid="metro-faq-heading"
                            >
                                {lang === "hi" ? `${metro.city} में ALBL — सवाल और जवाब।` : `ALBL IN ${metro.city.toUpperCase()} — QUESTIONS, ANSWERED.`}
                            </h2>
                        </Reveal>
                        <Reveal delay={120}>
                            <Accordion type="single" collapsible className="mt-10" data-testid="metro-faq-accordion">
                                {faqs.map((f, i) => (
                                    <AccordionItem key={f.q} value={`mfaq-${i}`} data-testid={`metro-faq-item-${i + 1}`}>
                                        <AccordionTrigger className="text-left font-display text-base sm:text-lg font-semibold text-slate-900 hover:text-[#1E3A8A]">
                                            {f.q}
                                        </AccordionTrigger>
                                        <AccordionContent className="text-slate-600 text-sm sm:text-base leading-relaxed">
                                            {f.a}
                                        </AccordionContent>
                                    </AccordionItem>
                                ))}
                            </Accordion>
                        </Reveal>
                    </div>
                </section>
            )}

            {lang === "hi" ? <HiFinalCta /> : <FinalCta />}
        </main>
    );
};

export default MetroMarket;
